/**
 * Derive a search-results array from a TanStack mutation's data field.
 *
 * Bug #18 (audit 2026-05-06): the dashboard kept the search results in
 * a separate ``useState`` slot and copied data over in
 * ``mutation.onSuccess``. That created two sources of truth — if the
 * mutation refired before a render, the local state lagged behind, and
 * if a stale onSuccess fired late it could overwrite a newer result.
 *
 * Routing the dashboard's ``searchResults`` through ``useMemo`` over
 * this helper collapses the duplication: the mutation owns the data,
 * and the rendered list is a pure function of it.
 *
 * Accepted shapes (in priority order):
 *   - ``{ markets: Array<...> }``
 *   - ``{ opportunities: Array<...> }``
 *   - bare ``Array<...>``
 *
 * Anything else returns ``[]`` so the UI never crashes on malformed
 * payloads.
 */
export function deriveSearchResults(
  data: unknown,
): Record<string, unknown>[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (typeof data !== "object") return [];

  const record = data as Record<string, unknown>;
  if (Array.isArray(record.markets)) {
    return record.markets as Record<string, unknown>[];
  }
  if (Array.isArray(record.opportunities)) {
    return record.opportunities as Record<string, unknown>[];
  }
  return [];
}
